import { type ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import darkLogo from '../../assets/helpers/navbar/tenantpay-white.png';
import { useAuth } from '../../auth/Auth';
import { getBuildingDetails } from '../../auth/_requests';
import { getAvatarInitials } from '../utils/utils';
import { useModal } from '../hooks/modalContext';
import { openDashboardPayRentFlow } from '../utils/payFlowGuard';
import { buildingDetails } from '../utils/types';
import clsx from 'clsx';

type NavGroupKey = 'home' | 'pay' | 'reward' | 'account';

type NavItem = {
  path: string;
  label: string;
  group: NavGroupKey;
  icon: ReactNode;
  badge?: string;
};

type NavSection = {
  label: string;
  items: NavItem[];
};

const NavIcons = {
  home: (
    <svg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg' aria-hidden>
      <path d='M12 2.5L2 10v11a1 1 0 001 1h6v-7h6v7h6a1 1 0 001-1V10L12 2.5z' />
    </svg>
  ),
  pay: (
    <svg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg' aria-hidden>
      <path d='M2 6a2 2 0 012-2h16a2 2 0 012 2v2H2V6zm0 4h20v8a2 2 0 01-2 2H4a2 2 0 01-2-2v-8zm3 6h4v2H5v-2z' />
    </svg>
  ),
  earn: (
    <svg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg' aria-hidden>
      <polygon points='12 2 14.9 8.5 22 9.3 16.6 14.1 18.2 21 12 17.4 5.8 21 7.4 14.1 2 9.3 9.1 8.5' />
    </svg>
  ),
  save: (
    <svg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg' aria-hidden>
      <path d='M21.4 11.6L12.4 2.6A2 2 0 0011 2H4a2 2 0 00-2 2v7a2 2 0 00.6 1.4l9 9a2 2 0 002.8 0l7-7a2 2 0 000-2.8zM7 9a2 2 0 110-4 2 2 0 010 4z' />
    </svg>
  ),
  redeem: (
    <svg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg' aria-hidden>
      <path d='M20 7h-3.2a3.5 3.5 0 00-4.8-4.6A3.5 3.5 0 007.2 7H4a2 2 0 00-2 2v3h9V9h2v3h9V9a2 2 0 00-2-2zM9.5 7a1.5 1.5 0 110-3c1 0 1.5 1 1.5 3H9.5zm5 0H13c0-2 .5-3 1.5-3a1.5 1.5 0 010 3zM3 14v6a2 2 0 002 2h6v-8H3zm10 0v8h6a2 2 0 002-2v-6h-8z' />
    </svg>
  ),
  reporting: (
    <svg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg' aria-hidden>
      <rect x='3' y='12' width='4' height='9' rx='1' />
      <rect x='10' y='6' width='4' height='15' rx='1' />
      <rect x='17' y='3' width='4' height='18' rx='1' />
    </svg>
  ),
  payments: (
    <svg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg' aria-hidden>
      <path d='M2 6a2 2 0 012-2h16a2 2 0 012 2v2H2V6zm0 4h20v8a2 2 0 01-2 2H4a2 2 0 01-2-2v-8zm3 6h4v2H5v-2z' />
    </svg>
  ),
  profile: (
    <svg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg' aria-hidden>
      <circle cx='12' cy='8' r='4' />
      <path d='M4 21a8 8 0 1116 0v1H4v-1z' />
    </svg>
  ),
} as const;

const topNavItems: NavItem[] = [
  { path: '/dashboard', label: 'Home', group: 'home', icon: NavIcons.home },
  { path: '/pay-rent', label: 'Pay rent', group: 'pay', icon: NavIcons.pay },
];

const navSections: NavSection[] = [
  {
    label: 'Rewards',
    items: [
      { path: '/rewards', label: 'Earn', group: 'reward', icon: NavIcons.earn },
      { path: '/rent-drop', label: 'Monthly rent drop', group: 'reward', icon: NavIcons.save },
      { path: '/wallet', label: 'Redeem', group: 'reward', icon: NavIcons.redeem },
    ],
  },
  {
    label: 'Account',
    items: [
      {
        path: '/rent-reporting',
        label: 'Rent reporting',
        group: 'account',
        icon: NavIcons.reporting,
      },
      {
        path: '/payment-methods',
        label: 'Payment methods',
        group: 'account',
        icon: NavIcons.payments,
      },
      { path: '/profile', label: 'Profile', group: 'account', icon: NavIcons.profile },
    ],
  },
];

const Sidebar = ({ closeSidebar }: { closeSidebar?: () => void }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { currentUser, auth } = useAuth();
  const { setModal, modalState } = useModal();
  const token = auth?.data.token;
  const [buildings, setBuildings] = useState<buildingDetails[]>([]);

  const hasProperty = Boolean(buildings[0]?.building.tenantBuildingId);

  useEffect(() => {
    const token = auth?.data.token;
    if (!token) return;

    const fetchBuildings = async () => {
      try {
        const response = await getBuildingDetails(token);
        setBuildings(response.data);
      } catch {
        setBuildings([]);
      }
    };

    void fetchBuildings();
  }, [auth?.data.token, modalState?.refreshProperties]);

  const firstName = currentUser?.user?.firstName?.trim() || '';
  const lastName = currentUser?.user?.lastName?.trim() || '';
  const displayName = firstName || currentUser?.user?.email?.split('@')[0] || 'Member';
  const avatarInitials = getAvatarInitials(firstName, lastName, currentUser?.user?.email);

  const isActive = (path: string) => pathname === path || pathname.startsWith(path + '/');

  const goTo = (path: string) => {
    if (path === '/pay-rent' && !hasProperty) {
      void openDashboardPayRentFlow(buildings, setModal, navigate, token, modalState);
      closeSidebar?.();
      return;
    }
    navigate(path);
    closeSidebar?.();
  };

  const renderNavItem = (item: NavItem) => {
    const active = isActive(item.path);
    const promote = !hasProperty && item.path === '/pay-rent' && !active;

    return (
      <div
        key={item.path}
        role='button'
        tabIndex={0}
        data-group={item.group}
        onClick={() => goTo(item.path)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            goTo(item.path);
          }
        }}
        className={clsx('ni', active && 'active', promote && 'ni-promote')}
      >
        <span className='ni-chip'>{item.icon}</span>
        <span className='ni-lb'>{item.label}</span>
        {promote ? <span className='ni-guide-badge'>Start</span> : null}
        {!promote && item.badge ? <span className='ni-badge'>{item.badge}</span> : null}
      </div>
    );
  };

  const goProfile = () => {
    goTo('/profile');
  };

  return (
    <aside className='sidebar'>
      <div className='sidebar-aurora' aria-hidden />
      <div className='sb-logo' style={{ padding: '18px 20px', justifyContent: 'center' }}>
        <button
          type='button'
          onClick={() => goTo('/dashboard')}
          className='flex items-center justify-center bg-transparent border-0 p-0 cursor-pointer'
        >
          <img
            src={darkLogo}
            alt='TenantPay'
            style={{ maxWidth: 140, height: 'auto', display: 'block' }}
          />
        </button>
      </div>

      <nav className='sb-nav'>
        {topNavItems.map(renderNavItem)}
        {navSections.map((section) => (
          <div key={section.label} className='sb-section'>
            <div className='sb-grp'>{section.label}</div>
            {section.items.map(renderNavItem)}
          </div>
        ))}
      </nav>

      <button
        type='button'
        className='sb-app'
        onClick={() => setModal('download-app')}
        aria-label='Download TenantPay app'
      >
        <div className='sb-app-phonewrap'>
          <div className='sb-app-glow' />
          <div className='sb-app-phone'>
            <div className='sb-app-phone-hdr' />
            <div className='sb-app-phone-line' />
            <div className='sb-app-phone-line short' />
          </div>
        </div>
        <div className='sb-app-right'>
          <p className='sb-app-lbl'>Get the TenantPay app</p>
          <p className='sb-app-sub'>TenantPay mobile</p>
          <div className='sb-app-stores'>
            <span className='sb-app-store'>
              <svg viewBox='0 0 24 24' fill='currentColor' aria-hidden>
                <path d='M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z' />
              </svg>
              iOS
            </span>
            <span className='sb-app-store'>
              <svg viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
                <path d='M3.61 2.99c-.22.13-.36.37-.36.63v16.76c0 .26.14.5.36.63l9.5-9.01-9.5-9.01zm10.36 9.81 2.39 2.27 3.92-2.23c.47-.27.47-.95 0-1.22l-3.92-2.23-2.39 2.41zm1.49 3.15-2.68-2.54-9.17 8.69c.23.1.5.09.72-.03l11.13-6.12zm-11.13-14c-.22-.12-.49-.13-.72-.03l9.17 8.69 2.68-2.54-11.13-6.12z' />
              </svg>{' '}
              Android
            </span>
          </div>
          <span className='sb-app-counter'>60,000+ members</span>
        </div>
      </button>

      <div className='sb-foot'>
        <button
          type='button'
          className='sb-user w-full text-left'
          onClick={goProfile}
          aria-label={`Open profile and settings for ${displayName}`}
        >
          <div className='sb-av' aria-hidden>
            {avatarInitials}
          </div>
          <div>
            <div className='sb-name'>{displayName}</div>
            <div className='sb-role'>Member</div>
          </div>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
